/**
 * Light / dark switches. Both read and write the one theme store
 * (src/contexts/ThemeContext.tsx), so they work anywhere: app, public site,
 * share links. English only.
 *
 *   ThemeToggle   an icon button for headers: shows the mode you would switch to
 *   ThemeSwitch   a labelled two-option control for Settings and menus
 */
import { Moon, Sun } from "lucide-react";
import { setAppTheme, useAppTheme, type Theme } from "@/contexts/ThemeContext";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface ThemeToggleProps {
  className?: string;
  /** Show the tooltip (needs a TooltipProvider above, as the app has). */
  tooltip?: boolean;
}

export function ThemeToggle({ className, tooltip = true }: ThemeToggleProps) {
  const theme = useAppTheme();
  const next: Theme = theme === "dark" ? "light" : "dark";
  const label = next === "light" ? "Switch to light mode" : "Switch to dark mode";
  const button = (
    <button
      type="button"
      onClick={() => setAppTheme(next)}
      aria-label={label}
      className={cn(
        "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-card/60 text-muted-foreground transition-colors hover:border-[hsl(var(--ec-line-strong))] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className,
      )}
    >
      {theme === "dark" ? <Sun className="h-4 w-4" aria-hidden /> : <Moon className="h-4 w-4" aria-hidden />}
    </button>
  );
  if (!tooltip) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="bottom">{next === "light" ? "Light mode" : "Dark mode"}</TooltipContent>
    </Tooltip>
  );
}

const OPTIONS: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
];

/** Both choices always visible: a switch that hides the other option is a riddle. */
export function ThemeSwitch({ className, size = "md" }: { className?: string; size?: "sm" | "md" }) {
  const theme = useAppTheme();
  return (
    <div role="radiogroup" aria-label="Appearance" className={cn("inline-flex items-center gap-1 rounded-full border border-border bg-card p-1", className)}>
      {OPTIONS.map(({ value, label, icon: Icon }) => {
        const on = theme === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => setAppTheme(value)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              size === "sm" ? "px-2.5 py-1 text-[12.5px]" : "px-3.5 py-1.5 text-sm",
              on ? "bg-primary/[0.12] text-primary ring-1 ring-inset ring-primary/40" : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Icon className={size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4"} aria-hidden />
            {label}
          </button>
        );
      })}
    </div>
  );
}

export default ThemeToggle;
